"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import type { NavTab } from "@prisma/client";

// 依目前路徑高亮對應頁籤。頁籤資料要從資料庫撈（NavBar 是 async server component），
// 但高亮、展開/收合都要看瀏覽器狀態，所以拆成這個小 client component，NavBar 撈完資料傳進來就好。
// 寬螢幕維持橫排頁籤；窄螢幕（sm 以下）改成漢堡選單展開的直式選單，不是加捲軸——
// 頁籤數量以後會持續增加，捲軸在手機上很難用，展開式選單才是一般手機頁面的做法。
export default function NavTabLinks({ tabs }: { tabs: NavTab[] }) {
  const pathname = usePathname();
  const [open, setOpen] = useState(false);

  // 換頁後自動收合選單，避免選單開著擋住下一頁內容
  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  function isActive(tab: NavTab) {
    return pathname === tab.href || pathname.startsWith(`${tab.href}/`);
  }

  return (
    <>
      <nav className="hidden sm:flex items-center gap-4 text-sm text-stone-600">
        {tabs.map((tab) => (
          <Link
            key={tab.id}
            href={tab.href}
            className={
              isActive(tab)
                ? "text-primary font-medium border-b-2 border-primary pb-0.5"
                : "hover:text-stone-900 border-b-2 border-transparent pb-0.5"
            }
          >
            {tab.label}
          </Link>
        ))}
      </nav>

      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-label={open ? "收合導覽選單" : "展開導覽選單"}
        className="sm:hidden inline-flex items-center justify-center w-9 h-9 rounded-lg text-stone-600 hover:bg-stone-100"
      >
        {open ? (
          <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M6 6l12 12M18 6L6 18" strokeLinecap="round" />
          </svg>
        ) : (
          <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M4 7h16M4 12h16M4 17h16" strokeLinecap="round" />
          </svg>
        )}
      </button>

      {open && (
        <nav className="sm:hidden absolute top-full left-0 right-0 bg-white border-b shadow-lg z-20 flex flex-col py-2">
          {tabs.map((tab) => (
            <Link
              key={tab.id}
              href={tab.href}
              className={`px-4 py-3 text-sm ${
                isActive(tab) ? "text-primary font-medium bg-primary/5" : "text-stone-600 hover:bg-stone-50"
              }`}
            >
              {tab.label}
            </Link>
          ))}
        </nav>
      )}
    </>
  );
}
